// T22.201 — hold the header's background reads until Discover's first page lands.
//
// MEASURED on staging (main.3ee61e07, logged-in Pro, 390 and 1440): a cold
// load of /discover fires explore page 1 in the SAME ~1 ms window as the
// notification feed, the notification unread count, the DM conversation list
// and the avatar's profile-wheel read. On the small staging API pool these
// five compete with explore, so explore p1 took 1.5-2.2 s in the browser while
// the same request alone took 0.31-0.39 s, and the first wheels painted at
// 2.4-3.4 s. Holding just those four reads until explore p1 answered painted
// the first wheels at 1.2-1.4 s (target < 1.5 s).
//
// Module-level on purpose: ExploreV2 is a React.lazy() chunk, so the header
// mounts (and would fire its queries) BEFORE the feed component even exists.
// The gate therefore starts CLOSED when the app boots on a Discover URL, the
// feed opens it when page 1 settles (data OR error), and a watchdog opens it
// anyway after MAX_HOLD_MS so a redirect/failed chunk never starves the badges.
// Every other boot URL starts OPEN, so nothing changes off Discover.

import { useSyncExternalStore } from "react";

export const MAX_HOLD_MS = 3000;

const DISCOVER_PATHS = ["/discover", "/explore", "/explore-v2"];

export const isDiscoverPath = (pathname) => {
  const p = (pathname || "").replace(/\/+$/, "") || "/";
  return DISCOVER_PATHS.includes(p);
};

let held = false;
let watchdog = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());

export const openFeedGate = () => {
  if (watchdog) {
    clearTimeout(watchdog);
    watchdog = null;
  }
  if (!held) return;
  held = false;
  notify();
};

export const holdFeedGate = () => {
  held = true;
  if (watchdog) clearTimeout(watchdog);
  watchdog = setTimeout(openFeedGate, MAX_HOLD_MS);
  notify();
};

export const isFeedGateOpen = () => !held;

// Hook-free form for a fetcher: resolves at once when open, else on open.
export const waitForFeedGate = () =>
  held
    ? new Promise((resolve) => {
        const fn = () => {
          if (held) return;
          listeners.delete(fn);
          resolve();
        };
        listeners.add(fn);
      })
    : Promise.resolve();

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useFeedGateOpen = () =>
  useSyncExternalStore(subscribe, isFeedGateOpen, isFeedGateOpen);

// T22.201 (round 2) -- MEASURED on web-stg main.cb4edecd (CDP CPU profile,
// 390 phone, 4x throttle): with explore p1 prefetched and the header reads
// gated, the first wheels still waited on main-thread work that is not the
// feed at all: Sentry Replay's rrweb recorder serializing every node the
// wheels add (~600 ms of the 7.9 s) and Google's reCAPTCHA script booting
// (~540 ms). Neither is needed on an anonymous Discover first paint, so on a
// Discover boot both wait for afterFeedFirstPaint(): the gate opening (page 1
// settled, or the watchdog) plus FIRST_PAINT_SETTLE_MS for the wheels to draw.
// Every other boot URL runs the callback synchronously -- nothing changes there.
export const FIRST_PAINT_SETTLE_MS = 1500;

export const bootedOnDiscover =
  typeof window !== "undefined" && isDiscoverPath(window.location?.pathname);

let settled = !bootedOnDiscover;
const settledListeners = new Set();
const pending = [];

const settle = () => {
  if (settled) return;
  settled = true;
  pending.splice(0).forEach((cb) => cb());
  settledListeners.forEach((fn) => fn());
};

// A plain timer, not requestIdleCallback: Chromium can report idle between the
// gate opening and the wheels' D3 draw, and Safari has no idle callback.
const scheduleSettle = () => setTimeout(settle, FIRST_PAINT_SETTLE_MS);

export const isFeedFirstPaintSettled = () => settled;

// Run `cb` once Discover's first wheels have had their paint. Synchronous when
// the app did not boot on Discover (or already settled).
export const afterFeedFirstPaint = (cb) => {
  if (settled) {
    cb();
    return;
  }
  pending.push(cb);
};

const subscribeSettled = (fn) => {
  settledListeners.add(fn);
  return () => settledListeners.delete(fn);
};

export const useFeedFirstPaintSettled = () =>
  useSyncExternalStore(subscribeSettled, isFeedFirstPaintSettled, isFeedFirstPaintSettled);

if (bootedOnDiscover) {
  holdFeedGate();
  waitForFeedGate().then(scheduleSettle);
}
